/**
Project: Voice Panel (c)
Title: New Subscriber Address 
Description: Component for adding the new subscriber address to the inventory.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/

import React, { Component } from "react";
import { connect } from "react-redux";
import IntlUtil from "../../../../core/helpers/intl-util";
import { Grid, Row, Col } from "react-flexbox-grid";
import {
  Text,
  FormLabel,
  Dialog,
  Button,
  Dropdown,
  Divider,
  Input,
  Segment,
  List,
} from "@fluentui/react-northstar";
import {
  setSubscriberAddress,
  searchAddress,
} from "../actions/subscriber-address-actions";
import {
  getCountryList
} from "../../common/actions/general-actions";
import axios from "axios";
import PageUtil from "../../../../core/helpers/page-util";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
} from "../../common/actions/general-actions";
import AlertSuccessMessage from "../../common/helpers/alert-success-message";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import { manageError } from "../../../../core/actions/common-actions";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import ResponseUtil from "../../../../core/helpers/response-util";
import { AppConfigProps } from "../../../../core/settings/app-config";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import PageLoader from "../../common/helpers/page-loader";

class NewSubscriberAddress extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_subscriber_address = "vpa_subscriber_address";

  constructor(props) {
    super(props);
    this.state = {
      newAddressFields: this.newSubscriberAddressFields(),
      stateName: null,
      showAlert: false,
      successMessage: "",
      errorMessage: "",
      errorDetails: "",
      isFormDataFilled: false,
      selectedValue: "",
      freeformAddress: "",
      isListDataFetched: false,
    };
  }
  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  clearAlert = async () => {
    await this.setStateAsync({
      errorMessage: null,
      errorDetails: null,
      successMessage: null,
      showAlert: false,
    });
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  newSubscriberAddressFields = () => {
    return {
      streetAddress1: "",
      streetAddress2: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
    };
  };

  checkFormData = async () => {
    if (
      this.state.newAddressFields?.streetAddress1.trim() !== "" &&
      this.state.newAddressFields?.city.trim() !== "" &&
      this.state.newAddressFields?.state.trim() !== "" &&
      this.state.newAddressFields?.country.trim() !== "" &&
      this.state.newAddressFields?.zipCode.trim() !== ""
    ) {
      await this.setStateAsync({ isFormDataFilled: true });
    } else {
      await this.setStateAsync({ isFormDataFilled: false });
    }
  };

  handleAddressSearchChange = async (e) => {
    let timer;
    clearTimeout(timer);
    if (
      e &&
      e.target.value &&
      e.target.value.length > 3 &&
      e.target.value !== ""
    ) {
      let value = e.target.value;
      await this.setStateAsync({ isListDataFetched: false });
      timer = await setTimeout(async () => {
        await searchAddress(value, this._cancelToken)
          .then(async (res) => {
            if (
              res &&
              res.data &&
              res.data.results &&
              res.status === AppConfigProps.httpStatusCode.ok
            ) {
              let freeformAddress = res.data.results.map((item) => {
                return (
                  item.address.freeformAddress + ", " + item.address.country
                );
              });
              let address = res.data.results.map((item) => {
                return item.address;
              });
              await this.setStateAsync({ freeformAddress: freeformAddress });
              await this.setStateAsync({ address: address });
              await this.setStateAsync({ isListDataFetched: true });
            }
          })
          .catch(async (err) => {
            // await manageError(err, this.props.history);
          });
      }, 600);
      await this.clearAlert();
    } else {
      await this.setStateAsync({ freeformAddress: "" });
    }
  };

  handleSelectChange = async (e) => {
    await this.setStateAsync({ selectedValue: e.target.value });
  };

  handleSelectClick = async (e) => {
    if (e && e.target.innerText) {
      let value = e.target.innerText;
      await this.setStateAsync({
        selectedValue: e.target.innerText,
        isDisabled: false,
      });
      if (this.state.address && this.state.address !== null) {
        let address = this.state.address.find((item) => {
          if (item.freeformAddress + ", " + item.country === value) {
            return true;
          } else {
            return false;
          }
        });
        if (address && address !== null) {
          let streetAddress1 =
            (address.streetNumber ? address.streetNumber + ", " : "") +
            (address.streetName || "");
          let streetAddress2 = address.municipalitySubdivision
            ? address.municipalitySubdivision
            : "";
          let city = address.localName
            ? address.localName
            : address.municipality
              ? address.municipality
              : "";
          let state = address.countrySubdivisionName
            ? address.countrySubdivisionName
            : address.countrySubdivision
              ? address.countrySubdivision
              : "";
          let country = address.country
            ? address.country
            : address.countryCode
              ? address.countryCode
              : "";
          let postalCode = address?.postalCode ? address.postalCode : "";
          await this.setStateAsync({
            newAddressFields: {
              ...this.state.newAddressFields,
              streetAddress1: streetAddress1?.trim(),
              streetAddress2: streetAddress2?.trim(),
              city: city.trim(),
              state: state.trim(),
              country: country.trim(),
              zipCode: postalCode.trim(),
            },
            selectedValue: "",
          });
        }
      }
      await this.setStateAsync({ freeformAddress: null });
      await this.checkFormData();
    }
    await this.clearAlert();
  };

  handleNewAddress = async (e, select) => {
    if (e && e.target && e.target.type === "text") {
      await this.setStateAsync({
        newAddressFields: {
          ...this.state.newAddressFields,
          [e.target.name]: e.target.value,
        },
      });
    }
    if (select && select.name === "country") {
      await this.setStateAsync({
        newAddressFields: {
          ...this.state.newAddressFields,
          [select.name]: select.value,
        },
      });
    }
    await this.clearAlert();
    await this.checkFormData();
  };

  saveSubscriberAddress = async () => {
    let address = {
      addLine1: this.state.newAddressFields?.streetAddress1
        ? this.state.newAddressFields.streetAddress1.trim()
        : null,
      addLine2: this.state.newAddressFields?.streetAddress2
        ? this.state.newAddressFields.streetAddress2.trim()
        : null,
      city: this.state.newAddressFields?.city
        ? this.state.newAddressFields.city.trim()
        : null,
      state: this.state.newAddressFields?.state
        ? this.state.newAddressFields.state.trim()
        : null,
      country: this.state.newAddressFields?.country
        ? this.state.newAddressFields.country.trim()
        : null,
      zip: this.state.newAddressFields?.zipCode
        ? this.state.newAddressFields.zipCode.trim()
        : null,
    };
    this.props.showPageWaitDialog(
      IntlUtil.getText(
        this._intl_ns_vpa_subscriber_address,
        "notification.info.pleaseWaitDialogAddressSave"
      )
    );
    setSubscriberAddress(address, this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.data &&
          res.data.result &&
          res.status === AppConfigProps.httpStatusCode.ok
        ) {
          await this.setStateAsync({
            newAddressFields: this.newSubscriberAddressFields(),
          });
          this.checkFormData();
          await this.props.fetchSubscriberAddress();
          await this.props.hidePageWaitDialog();
          await this.setStateAsync({
            stateName: null,
            successMessage: IntlUtil.getText(
              this._intl_ns_vpa_subscriber_address,
              "notification.success.successResponseForSavedAddress"
            ),
            showAlert: true,
          });
        }
      })
      .catch(async (err) => {
        await this.props.hidePageWaitDialog();
        const errorStatus = ResponseUtil.getStatus(err);
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
            VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
          ) {
            this.props.history.push(VoiceAdminURLProps.setup);
          } else if (
            primeError &&
            primeError.code ===
            VoiceAdminConstants.SERVER_ERROR_CODE.ONE_BILL_SAVE_FAILED
          ) {
            await this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_subscriber_address,
                "notification.error.errorResponse"
              ),
              showAlert: true,
            });
            await this.setStateAsync({
              errorDetails: primeError.message,
            });
          }
        } else {
          await manageError(err, this.props.history);
        }
      });
  };

  render() {
    return (
      <Dialog
        className="vpa-bn-dialog"
        backdrop={true}
        content={
          <>
            <Text className="page-text text-semibold vpa-dialog-header text-fc-primary">
              {IntlUtil.getText(
                this._intl_ns_vpa_subscriber_address,
                "content.addNewAddress"
              )}
            </Text>
            <Divider size={1} />
            <Grid fluid className="page-grid m-l-0 p-l-0">
              <Row className="m-t-5 m-l-0 p-l-0">
                {this.state.showAlert === true &&
                  this.state.errorMessage &&
                  this.state.errorMessage.length > 0 ? (
                  <Col>
                    <AlertErrorMessage
                      content={this.state.errorMessage}
                      moreDetails={{
                        label: IntlUtil.getText(
                          this._intl_ns_vpa_subscriber_address,
                          "content.moreDetails"
                        ),
                        content: this.state.errorDetails,
                      }}
                    />{" "}
                  </Col>
                ) : null}
                {this.state.successMessage &&
                  this.state.showAlert === true &&
                  this.state.successMessage.length > 0 ? (
                  <Col>
                    <AlertSuccessMessage content={this.state.successMessage} />
                  </Col>
                ) : null}
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xl={9} md={12} lg={11} xs={12} className="vpa-bn-text">
                  <Input
                    type="search"
                    placeholder="Search Address"
                    inverted
                    className="vpa-inventory-input"
                    value={this.state.selectedValue}
                    onChange={this.handleAddressSearchChange}
                    onKeyPress={this.handleAddressSearchChange}
                    onInput={this.handleSelectChange}
                  />
                  {this.state.freeformAddress &&
                    this.state.freeformAddress.length > 0 &&
                    this.state.freeformAddress !== "" ? (
                    <Segment
                      className="vpa-autoComplete"
                      content={
                        <PageLoader
                          status={this.state.isListDataFetched}
                          size="small"
                          labelPosition="below"
                          label={IntlUtil.getText(
                            this._intl_ns_common,
                            "content.loadingInprogress"
                          )}
                          type="inline"
                        >
                          <List
                            selectable
                            items={this.state.freeformAddress}
                            onClick={this.handleSelectClick}
                            className="vpa-list"
                          />
                        </PageLoader>
                      }
                    />
                  ) : null}
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={5} lg={4} xl={3} className="vpa-bn-text">
                  <FormLabel
                    required
                    className="page-text text-semibold text-fc-dark"
                  >
                    {IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.streetAddress1"
                    )}
                  </FormLabel>
                </Col>
                <Col xs={12} md={7} lg={7} xl={6}>
                  <Input
                    maxLength="256"
                    inverted
                    name="streetAddress1"
                    className="vpa-inventory-input"
                    value={this.state.newAddressFields.streetAddress1}
                    onChange={this.handleNewAddress.bind(this)}
                  />
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={5} lg={4} xl={3} className="vpa-bn-text">
                  <FormLabel className="page-text text-semibold text-fc-dark">
                    {IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.streetAddress2"
                    )}
                  </FormLabel>
                </Col>
                <Col xs={12} md={7} lg={7} xl={6}>
                  <Input
                    maxLength="256"
                    inverted
                    name="streetAddress2"
                    className="vpa-inventory-input"
                    value={this.state.newAddressFields.streetAddress2}
                    onChange={this.handleNewAddress.bind(this)}
                  />
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={5} lg={4} xl={3} className="vpa-bn-text">
                  <FormLabel
                    required
                    className="page-text text-semibold text-fc-dark"
                  >
                    {IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.city"
                    )}
                  </FormLabel>
                </Col>
                <Col xs={12} md={7} lg={7} xl={6}>
                  <Input
                    maxLength="32"
                    inverted
                    name="city"
                    className="vpa-inventory-input"
                    value={this.state.newAddressFields.city}
                    onChange={this.handleNewAddress.bind(this)}
                  />
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={5} lg={4} xl={3} className="vpa-bn-text">
                  <FormLabel
                    required
                    className="page-text text-semibold text-fc-dark"
                  >
                    {IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.stateOrProvince"
                    )}
                  </FormLabel>
                </Col>
                <Col xs={12} md={7} lg={7} xl={6}>
                  <Input
                    inverted
                    maxLength="32"
                    name="state"
                    className="vpa-inventory-input"
                    value={this.state.newAddressFields.state}
                    onChange={this.handleNewAddress.bind(this)}
                  />
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={5} lg={4} xl={3} className="vpa-bn-text">
                  <FormLabel
                    required
                    className="page-text text-semibold text-fc-dark"
                  >
                    {IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.country"
                    )}
                  </FormLabel>
                </Col>
                <Col xs={12} md={7} lg={7} xl={6}>
                  <Dropdown
                    items={getCountryList(this.props.countryStatesData)}
                    name="country"
                    placeholder="Select Country"
                    className="vpa-dropdown"
                    inverted={true}
                    value={this.state.newAddressFields.country}
                    onChange={this.handleNewAddress.bind(this)}
                  />
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={5} lg={4} xl={3} className="vpa-bn-text">
                  <FormLabel
                    required
                    className="page-text text-semibold text-fc-dark"
                  >
                    {IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.zipOrPostal"
                    )}
                  </FormLabel>
                </Col>
                <Col xs={12} md={7} lg={7} xl={6}>
                  <Input
                    maxLength="32"
                    id="zipCode"
                    name="zipCode"
                    className="vpa-inventory-input vpa-bn-input vpa-address-btn"
                    inverted={true}
                    value={this.state.newAddressFields.zipCode}
                    onChange={this.handleNewAddress.bind(this)}
                  />
                </Col>
              </Row>
              <Row className="m-t-20 m-l-0">
                <Col className="m-r-10 m-m-10">
                  <Button
                    onClick={this.saveSubscriberAddress}
                    disabled={!this.state.isFormDataFilled}
                    primary
                    className="vpa-save-btn"
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.save"
                    )}
                  />
                </Col>
                <Col className="m-r-10 m-m-10">
                  <Button
                    onClick={() => this.props.hideDialogBox()}
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_subscriber_address,
                      "content.close"
                    )}
                  />
                </Col>
              </Row>
            </Grid>
          </>
        }
        open={this.props.showDialogBox}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  subscriberAccount: state.subscriberStore.subscriberAccount,
});
const mapActionToProps = {
  showPageWaitDialog,
  hidePageWaitDialog,
};

export default connect(mapStateToProps, mapActionToProps)(NewSubscriberAddress);
